/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { NgClass } from "@angular/common";
import { Component, OnDestroy, OnInit, signal, TemplateRef, ViewChild } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { EChartsOption } from "echarts";
import {
	filter,
	finalize,
	forkJoin,
	last,
	of,
	repeat,
	Subject,
	switchMap,
	takeUntil,
	takeWhile,
	tap,
	throwError
} from "rxjs";
import { AccordionGroupComponent } from "../../../shared/components/accordion-group/accordion-group.component";
import { AccordionComponent } from "../../../shared/components/accordion-group/accordion/accordion.component";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { ChartComponent } from "../../../shared/components/chart/chart.component";
import { ChipsComponent } from "../../../shared/components/chips/chips.component";
import { DropdownComponent } from "../../../shared/components/dropdown/dropdown.component";
import { InputComponent } from "../../../shared/components/input/input.component";
import { LayerConfigurationComponent } from "../../../shared/components/layer-configuration/layer-configuration.component";
import { BarChart } from "../../../shared/models/http/charts/bar-chart/bar-chart.model";
import { FilterApplication } from "../../../shared/models/http/dashboard/filter-page-response.model";
import { ScenarioInfo } from "../../../shared/models/http/what-if/scenario-info.model";
import { ScenarioLayer } from "../../../shared/models/http/what-if/scenario/scenario-layer.model";
import { ApplicationError } from "../../../shared/models/ui/application-error.model";
import { ChartPattern, ChartResponse } from "../../../shared/models/ui/charts.model";
import { ChipOption } from "../../../shared/models/ui/chip-option.model";
import { SelectOption } from "../../../shared/models/ui/select-option.model";
import { DashboardSoftwareAssetsService } from "../../../shared/services/dashboard-software-assets.service";
import { DialogService } from "../../../shared/services/dialog.service";
import { StorageService } from "../../../shared/services/storage.service";
import { WhatIfScenarioService } from "../../../shared/services/what-if-scenario.service";
import { WhatIfSimulationService } from "../../../shared/services/what-if-simulation.service";
import { WhatIfService } from "../../../shared/services/what-if.service";
import { COLORS, PATHS, PREDICTION_WINDOWS } from "../../../shared/utils/constants";
import {
	CHART_PATTERNS,
	SCENARIO_DIFFERENCES_BAR_CHART_OVERRIDES
} from "../../../shared/utils/echarts/chart-constants";

@Component({
	selector: "globalictcc-whatif-software-assets",
	standalone: true,
	imports: [
		ButtonComponent,
		ReactiveFormsModule,
		DropdownComponent,
		ChartComponent,
		AccordionGroupComponent,
		AccordionComponent,
		LayerConfigurationComponent,
		InputComponent,
		ChipsComponent,
		NgClass
	],
	templateUrl: "./whatif-software-assets.component.html",
	styleUrl: "./whatif-software-assets.component.scss"
})
export class WhatifSoftwareAssetsComponent implements OnInit, OnDestroy {
	@ViewChild("newScenarioDialogContent") public newScenarioDialogContent: TemplateRef<unknown>;

	public scenariosChips: ChipOption[] = [];
	public selectedScenario: string = "";
	public isEditable: boolean = true;
	public firstNonBaselineScenarioId: number;

	public baselineScenarioLayers: ScenarioLayer[] = [];
	public customScenarioLayers: ScenarioLayer[] = [];

	public applicationForm = new FormGroup({
		applicationId: new FormControl("", [Validators.required]),
		predictionWindow: new FormControl("6M")
	});

	public scenarioRunForm = new FormGroup({
		scenario: new FormControl("", [Validators.required])
	});

	public newScenarioForm = new FormGroup({
		startingScenario: new FormControl(null, [Validators.required]),
		scenarioName: new FormControl("", [Validators.required])
	});

	public isApplicationFormApplied: boolean = false;
	public isScenarioVisible: boolean = false;
	public isRunScenarioButtonVisible: boolean = false;
	public isScenarioRunned: boolean = false;

	public scenariosDifferencesBarChart?: ChartResponse<BarChart>;
	public baselineScenarioLayersBarChart?: ChartResponse<BarChart>;
	public baselineScenarioComparisonBarChart?: ChartResponse<BarChart>;
	public isScenariosDifferencesBarChartLoading: boolean = false;
	public isBaselineScenarioLayersBarChartLoading: boolean = false;
	public isBaselineScenarioComparisonBarChartLoading: boolean = false;
	public baselineScenarioComparisonBarChartPatterns: ChartPattern[] = [
		null,
		CHART_PATTERNS.DIAGONAL_LINES
	];
	public scenariosDifferencesBarChartPatterns: ChartPattern[] = [
		null,
		CHART_PATTERNS.DIAGONAL_LINES
	];
	public scenariosDifferencesBarChartOverrides: EChartsOption =
		SCENARIO_DIFFERENCES_BAR_CHART_OVERRIDES;
	public baselineScenarioLayersBarChartOverrides: EChartsOption = {
		series: {
			label: {
				show: true
			}
		}
	};
	public baselineScenarioComparisonBarChartOverrides: EChartsOption = {};

	public scenariosOptions: SelectOption[] = [];
	public scenarioRunOptions: SelectOption[] = [];
	public applicationOptions: SelectOption[] = [];
	public hasMoreApplications: boolean = false;
	public isApplicationsLoadingMore: boolean = false;
	public predictionWindows: SelectOption[] = PREDICTION_WINDOWS;

	private _tenantId: string = "";
	private _baselineScenarioComparisonBarChart$ = new Subject<BarChart>();
	private _destroy$ = new Subject<void>();
	private _predictionWindow: string = "6M";
	private _applicationId: string = "";
	private _isCreateScenarioButtonDisabled = signal(true);
	private _applicationsCursor: string | null = null;
	private _lastApplicationSearchTerm: string = "";

	constructor(
		private _whatIfService: WhatIfService,
		private _whatIfScenarioService: WhatIfScenarioService,
		private _whatIfSimulationService: WhatIfSimulationService,
		private _storageService: StorageService,
		private _dashboardSoftwareAssetsService: DashboardSoftwareAssetsService,
		private _dialogService: DialogService,
		private _router: Router,
		private _route: ActivatedRoute
	) {}

	ngOnInit() {
		this._tenantId = this._storageService.getTenantId();

		this._fetchApplicationIds("", null);

		this._onBaselineScenarioComparisonBarChartChange();
		this._onQueryParamsChange();
		this._onCreateScenarioFormStatusChange();
		this._onScenarioToRunSelectionChange();
	}

	ngOnDestroy() {
		this._destroy$.next();
		this._destroy$.complete();
	}

	public onApplicationSearch(term: string): void {
		this._applicationsCursor = null;
		this._fetchApplicationIds(term, null);
	}

	public onApplicationLoadMore(): void {
		if (!this.hasMoreApplications || this.isApplicationsLoadingMore) return;
		this._fetchApplicationIds(this._lastApplicationSearchTerm, this._applicationsCursor, true);
	}

	private _fetchApplicationIds(
		searchTerm: string,
		cursor: string | null,
		isLoadingMore: boolean = false
	): void {
		if (!isLoadingMore) {
			this.applicationOptions = [];
		}
		this.isApplicationsLoadingMore = true;
		this._lastApplicationSearchTerm = searchTerm;

		this._dashboardSoftwareAssetsService
			.getApplicationsIds(searchTerm, cursor)
			.pipe(
				takeUntil(this._destroy$),
				finalize(() => (this.isApplicationsLoadingMore = false))
			)
			.subscribe(response => {
				const mapped = response.content.map((app: FilterApplication) => ({
					label: app.name,
					value: app.name
				}));
				this.applicationOptions = isLoadingMore
					? [...this.applicationOptions, ...mapped]
					: mapped;
				this.hasMoreApplications = response.hasNext;
				this._applicationsCursor = response.nextCursor ?? null;
			});
	}

	/**
	 * Handles changes to the baseline scenario comparison bar chart observable.
	 * Subscribes to the `_baselineScenarioComparisonBarChart$` observable and updates
	 * the `baselineScenarioComparisonBarChartOverrides` property with the result of
	 * `_getBaselineScenarioComparisonBarChartOverrides` when a new value is emitted.
	 * The subscription is automatically unsubscribed when the `_destroy$` observable emits.
	 *
	 * @private
	 */
	private _onBaselineScenarioComparisonBarChartChange() {
		this._baselineScenarioComparisonBarChart$
			.pipe(
				tap({
					next: barChart =>
						(this.baselineScenarioComparisonBarChartOverrides =
							this._getBaselineScenarioComparisonBarChartOverrides(barChart))
				}),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	/**
	 * Handles changes to the query parameters in the route.
	 *
	 * This method listens to the query parameters from the route and performs the following actions:
	 * - Updates the `applicationForm` with `applicationId` and `predictionWindow` if `applicationId` is defined.
	 * - Calls `_onApplicationApply` to fetch section data and combines it with the `scenarioId` using `forkJoin`.
	 * - Filters out null or undefined values from the observable stream.
	 * - Updates the `scenariosChips` selection and sets the `selectedScenario` based on the `scenarioId`.
	 * - Ensures cleanup by unsubscribing when the component is destroyed using `takeUntil`.
	 *
	 * @private
	 */
	private _onQueryParamsChange() {
		this._route.queryParams
			.pipe(
				switchMap(queryParams => {
					const { applicationId, predictionWindow, scenarioId } = queryParams;

					if (applicationId === undefined || applicationId === null) return of(null);

					this.applicationForm.patchValue({ applicationId, predictionWindow });

					const sectionsData$ = this._onApplicationApply() || of(null);

					return forkJoin([sectionsData$, of(scenarioId)]);
				}),
				filter(Boolean),
				tap({
					next: ([, scenarioId]) => {
						if (scenarioId !== undefined || scenarioId !== null) {
							this.scenariosChips = this.scenariosChips.map(chip => ({
								...chip,
								selected: chip.value === +scenarioId
							}));

							const selectedScenario = this.scenariosChips.find(
								chip => chip.selected
							);

							this.onScenarioClick(
								selectedScenario?.value || this.scenariosChips[0].value
							);
						}
					}
				}),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	/**
	 * Handles the selection change event for the scenario to run.
	 *
	 * This method subscribes to the value changes of the scenario control in the scenarioRunForm.
	 * When the value changes, it checks if both the scenario value and applicationId are present.
	 * If they are, it fetches the scenario information from the _whatIfScenarioService.
	 *
	 * The subscription updates the visibility of the run scenario button based on the scenario status.
	 * If the scenario status is "DONE", it updates the baselineScenarioComparisonBarChart and sets isScenarioRunned to true.
	 *
	 * @private
	 */
	private _onScenarioToRunSelectionChange() {
		this.scenarioRunForm.controls.scenario.valueChanges
			.pipe(
				filter(Boolean),
				switchMap(value => {
					const applicationId = this.applicationForm.controls.applicationId.value;

					if (!applicationId) return of(null);

					this.isBaselineScenarioComparisonBarChartLoading = true;
					this.scenarioRunForm.controls.scenario.disable({ emitEvent: false });

					return this._whatIfScenarioService.getScenarioInfo(
						applicationId,
						+value,
						this._tenantId,
						this._predictionWindow
					);
				}),
				filter(Boolean),
				tap({
					next: scenario => {
						this.isRunScenarioButtonVisible = scenario.status === "CREATED";
						this.isScenarioRunned = scenario.status === "DONE";

						if (!this.isRunScenarioButtonVisible) {
							this.baselineScenarioComparisonBarChart = {
								chart: this._mergeBaselineAndCustomScenarioBarCharts(
									scenario.barChart,
									scenario.name
								),
								hasError: false
							};
							this._baselineScenarioComparisonBarChart$.next(
								this.baselineScenarioComparisonBarChart.chart
							);
						}

						this.isBaselineScenarioComparisonBarChartLoading = false;
						this.scenarioRunForm.controls.scenario.enable({ emitEvent: false });
					},
					error: () => {
						this.baselineScenarioComparisonBarChart = {
							chart: {} as BarChart,
							hasError: true
						};
					},
					finalize: () => {
						this.isBaselineScenarioComparisonBarChartLoading = false;
						this.scenarioRunForm.controls.scenario.enable({ emitEvent: false });
					}
				}),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	/**
	 * Determines whether the necessary conditions are met to fetch section data.
	 *
	 * This method checks if the required application ID and tenant ID are available.
	 * If these conditions are satisfied, it sets loading indicators for the scenarios
	 * differences bar chart and the baseline scenario layers bar chart, and returns `true`.
	 * Otherwise, it returns `false`.
	 *
	 * @returns `true` if the application ID and tenant ID are present, allowing
	 *                    data fetching; otherwise, `false`.
	 */
	private _canFetchSectionsData() {
		this._applicationId = this.applicationForm.controls.applicationId.value || "";
		this._predictionWindow = this.applicationForm.controls.predictionWindow.value || "6M";

		if (!this._applicationId || !this._tenantId) return false;

		this.isScenariosDifferencesBarChartLoading = true;
		this.isBaselineScenarioLayersBarChartLoading = true;

		return true;
	}

	/**
	 * Fetches and processes the data required for the "What If" sections.
	 *
	 * This method performs the following steps:
	 * 1. Checks if the data can be fetched using `_canFetchSectionsData`.
	 * 2. Retrieves user-specific "What If" data from the `_whatIfService`.
	 * 3. Updates the UI state based on the retrieved data, including scenarios and bar chart information.
	 * 4. Fetches additional scenario information for the baseline and first non-baseline scenarios, if available.
	 * 5. Updates the state with the retrieved scenario details, including layers, bar charts, and editability status.
	 *
	 * The method uses RxJS operators to handle asynchronous data fetching, error handling, and cleanup:
	 * - `tap`: Updates the UI state based on the data or errors.
	 * - `switchMap`: Fetches additional scenario information based on the retrieved data.
	 * - `filter`: Ensures only valid data is processed further.
	 * - `takeUntil`: Cleans up subscriptions when the component is destroyed.
	 *
	 * @private
	 * @returns An observable that emits the fetched data or `null` if no baseline scenario is found.
	 */
	private _fetchSectionsData() {
		if (!this._canFetchSectionsData()) return;

		return this._whatIfService
			.getUserWhatIfData(this._applicationId, this._tenantId, this._predictionWindow)
			.pipe(
				tap({
					next: whatIfData => {
						this.isApplicationFormApplied = true;
						this.isScenarioVisible = whatIfData.scenarios.length > 1;
						this.scenariosDifferencesBarChart = {
							chart: whatIfData.barChart,
							hasError: false
						};

						this._updateScenarios(whatIfData.scenarios);
					},
					error: () => {
						this.scenariosDifferencesBarChart = {
							chart: {} as BarChart,
							hasError: true
						};
					},
					finalize: () => (this.isScenariosDifferencesBarChartLoading = false)
				}),
				switchMap(whatIfData => {
					const baselineScenario = whatIfData.scenarios.find(
						scenario => scenario.isBaseline
					);
					const firstNonBaselineScenario = whatIfData.scenarios.find(
						scenario => !scenario.isBaseline
					);

					if (baselineScenario) {
						const getBaselineScenarioInfo = this._whatIfScenarioService.getScenarioInfo(
							this._applicationId,
							baselineScenario.id,
							this._tenantId,
							this._predictionWindow
						);

						if (!firstNonBaselineScenario) return getBaselineScenarioInfo;

						this.firstNonBaselineScenarioId = firstNonBaselineScenario.id;

						const getFirstNonBaselineScenarioInfo =
							this._whatIfScenarioService.getScenarioInfo(
								this._applicationId,
								firstNonBaselineScenario.id,
								this._tenantId,
								this._predictionWindow
							);

						return forkJoin([getBaselineScenarioInfo, getFirstNonBaselineScenarioInfo]);
					}

					return of(null);
				}),
				filter(Boolean),
				tap({
					next: response => {
						if (Array.isArray(response)) {
							const [baselineScenarioInfo, firstNonBaselineScenarioInfo] = response;

							this.baselineScenarioLayers = baselineScenarioInfo.layers;
							this.baselineScenarioLayersBarChart = {
								chart: baselineScenarioInfo.barChart,
								hasError: false
							};
							this.customScenarioLayers = firstNonBaselineScenarioInfo.layers;
							this.selectedScenario = this.firstNonBaselineScenarioId.toString();
							this.isEditable = firstNonBaselineScenarioInfo.status === "CREATED";
						} else {
							const scenario = response;

							this.baselineScenarioLayers = scenario.layers;
							this.baselineScenarioLayersBarChart = {
								chart: scenario.barChart,
								hasError: false
							};
							this.customScenarioLayers = [];
							this.selectedScenario = "";
							this.isEditable = false;
						}
					},
					error: () => {
						this.baselineScenarioLayersBarChart = {
							chart: {} as BarChart,
							hasError: true
						};
					},
					finalize: () => (this.isBaselineScenarioLayersBarChartLoading = false)
				}),
				takeUntil(this._destroy$)
			);
	}

	/**
	 * Handles the application of changes or updates within the component.
	 *
	 * This method resets the state of the scenario by marking it as not run
	 * and clearing the baseline scenario comparison bar chart data.
	 * It then fetches the updated sections data to reflect the changes.
	 *
	 * @returns A promise or observable that resolves when the sections data is fetched.
	 */
	private _onApplicationApply() {
		this.isScenarioRunned = false;
		this.baselineScenarioComparisonBarChart = undefined;

		return this._fetchSectionsData();
	}

	/**
	 * Handles the click event for applying an application.
	 * Invokes the `_onApplicationApply` method and subscribes to its observable result.
	 * This method is typically used to trigger application-specific logic when the apply button is clicked.
	 */
	public onApplicationApplyClick() {
		this._onApplicationApply()?.subscribe();
	}

	/**
	 * Updates the scenario-related data structures used in the component.
	 *
	 * This method processes the provided array of scenarios and updates three properties:
	 * - `scenariosChips`: A list of scenarios excluding the baseline, formatted for use as chips.
	 * - `scenariosOptions`: A list of all scenarios formatted as options for a dropdown.
	 * - `scenarioRunOptions`: A list of scenarios excluding the baseline, formatted as options for a dropdown.
	 *
	 * @param scenarios - An array of `ScenarioInfo` objects representing the scenarios to process.
	 * Each scenario contains information such as its name, ID, and whether it is a baseline scenario.
	 */
	private _updateScenarios(scenarios: ScenarioInfo[]) {
		this.scenariosChips = scenarios
			.filter(scenario => !scenario.isBaseline)
			.map((scenario, index) => ({
				label: scenario.name,
				value: scenario.id,
				selected: index === 0
			}));

		this.scenariosOptions = scenarios.map(scenario => ({
			label: scenario.name,
			value: scenario.id.toString()
		}));

		this.scenarioRunOptions = scenarios
			.filter(scenario => !scenario.isBaseline)
			.map(scenario => ({
				label: scenario.name,
				value: scenario.id.toString()
			}));
	}

	/**
	 * Opens a dialog to add a new scenario.
	 * The dialog will display a confirmation message with options to create or cancel.
	 * The dialog content is provided by `this.newScenarioDialogContent`.
	 */
	public onAddNewScenario() {
		this._dialogService
			.open({
				data: {
					type: "confirm",
					title: "Add New Scenario",
					isIconVisible: true,
					confirmBtnText: "Create",
					closeBtnText: "Cancel",
					content: this.newScenarioDialogContent,
					confirmAction: this._onCreateScenario.bind(this),
					isConfirmActionDisabled: this._isCreateScenarioButtonDisabled
				}
			})
			.afterClosed()
			.pipe(
				tap({
					next: () => this.newScenarioForm.reset()
				}),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	/**
	 * Handles the creation of a new scenario.
	 *
	 * This method retrieves the application ID, starting scenario ID, and new scenario name
	 * from the respective form controls. If all values are present, it calls the
	 * `createNewScenario` method of the `_whatIfScenarioService` to create a new scenario.
	 *
	 * Upon successful creation, the new scenario is added to the `scenariosChips` array.
	 *
	 * @private
	 */
	private _onCreateScenario() {
		const applicationId = this.applicationForm.controls.applicationId.value;
		const startingScenarioId = this.newScenarioForm.controls.startingScenario.value;
		const newScenarioName = this.newScenarioForm.controls.scenarioName.value;

		if (applicationId && newScenarioName && startingScenarioId) {
			this._whatIfScenarioService
				.createNewScenario(
					applicationId,
					+startingScenarioId,
					this._tenantId,
					newScenarioName
				)
				.pipe(
					tap({
						next: scenario => {
							const createdScenario = {
								label: newScenarioName,
								value: scenario.id,
								selected: this.scenariosChips.length === 0
							};

							this._addCreatedScenarioToUI(createdScenario);

							if (this.scenariosChips.length === 1)
								this.selectedScenario = scenario.id.toString();

							this.isScenarioVisible = this.scenariosChips.length > 0;
						}
					}),
					switchMap(() => {
						if (this.scenariosChips.length !== 1) {
							return of(null);
						}

						return this._whatIfScenarioService
							.getScenarioInfo(
								applicationId,
								+this.selectedScenario,
								this._tenantId,
								this._predictionWindow
							)
							.pipe(
								tap({
									next: scenario => {
										if (this.scenariosChips.length === 1)
											this.isEditable = scenario.status === "CREATED";

										this.customScenarioLayers = scenario.layers;
									}
								}),
								takeUntil(this._destroy$)
							);
					}),
					takeUntil(this._destroy$)
				)
				.subscribe();
		}
	}

	/**
	 * Adds a newly created scenario to the UI by updating the relevant arrays.
	 *
	 * @param createdScenario - The scenario to be added, represented as a `ChipOption` object.
	 *   - `label`: The display label of the scenario.
	 *   - `value`: The unique identifier of the scenario.
	 *
	 * Updates:
	 * - Appends the `createdScenario` to the `scenariosChips` array.
	 * - Adds a new option to the `scenariosOptions` array with the scenario's label and value.
	 * - Adds a new option to the `scenarioRunOptions` array with the scenario's label and value.
	 */
	private _addCreatedScenarioToUI(createdScenario: ChipOption) {
		this.scenariosChips = [...this.scenariosChips, createdScenario];

		this.scenariosOptions.push({
			label: createdScenario.label,
			value: createdScenario.value.toString()
		});

		this.scenarioRunOptions.push({
			label: createdScenario.label,
			value: createdScenario.value.toString()
		});
	}

	/**
	 * Handles the click event for a scenario chip.
	 *
	 * @param chipSelection - The selected scenario chip, which can be a string or number.
	 *
	 * This method sets the selected scenario, retrieves the application ID and prediction window
	 * from the form controls, and then calls the service to get scenario information if both
	 * application ID and prediction window are available. The retrieved scenario information
	 * is then used to update the custom scenario layers.
	 */
	public onScenarioClick(chipSelection: string | number) {
		this.selectedScenario = chipSelection.toString();

		const applicationId = this.applicationForm.controls.applicationId.value;
		const predictionWindow = this.applicationForm.controls.predictionWindow.value;
		const scenarioId = +this.selectedScenario;

		if (applicationId && predictionWindow) {
			this._whatIfScenarioService
				.getScenarioInfo(applicationId, scenarioId, this._tenantId, predictionWindow)
				.pipe(
					tap({
						next: scenario => {
							this.isEditable = scenario.status === "CREATED";
							this.customScenarioLayers = scenario.layers;
						}
					}),
					takeUntil(this._destroy$)
				)
				.subscribe();
		}
	}

	/**
	 * Initiates the scenario run process by calling the simulation service and then
	 * periodically checks the status of the simulation until it is complete. Once the
	 * simulation is done, it retrieves the scenario information and updates the component state.
	 *
	 * @remarks
	 * - This method first retrieves the application ID and scenario ID from the respective forms.
	 * - It then calls the `runSimulation` method of the `_whatIfSimulationService`.
	 * - After initiating the simulation, it periodically checks the simulation status every 5 seconds
	 *   using the `getScenarioSimulationStatus` method of the `_whatIfScenarioService`.
	 * - Once the status is "DONE", it retrieves the scenario information using the `getScenarioInfo` method.
	 * - Finally, it updates the component state with the retrieved scenario information.
	 */
	public onScenarioRun() {
		const applicationId = this.applicationForm.controls.applicationId.value;
		const scenarioId = this.scenarioRunForm.controls.scenario.value;

		if (applicationId && scenarioId) {
			this.isBaselineScenarioComparisonBarChartLoading = true;
			this.scenarioRunForm.controls.scenario.disable({ emitEvent: false });
			this.isScenarioRunned = true;

			this._whatIfSimulationService
				.runSimulation(+scenarioId, this._tenantId)
				.pipe(
					switchMap(() =>
						this._whatIfScenarioService
							.getScenarioSimulationStatus(applicationId, +scenarioId, this._tenantId)
							.pipe(
								repeat({ delay: 5000 }),
								takeWhile(
									response =>
										response.status !== "DONE" && response.status !== "ERROR",
									true
								),
								last()
							)
					),
					switchMap(({ status }) => {
						if (status === "ERROR")
							return throwError(
								() =>
									new ApplicationError(
										"Error while running simulation",
										this.constructor.name,
										this.onScenarioRun.name,
										false
									)
							);

						return this._whatIfScenarioService.getScenarioInfo(
							applicationId,
							+scenarioId,
							this._tenantId,
							this._predictionWindow
						);
					}),
					tap({
						next: scenario => {
							this.isRunScenarioButtonVisible = false;

							this.baselineScenarioComparisonBarChart = {
								chart: this._mergeBaselineAndCustomScenarioBarCharts(
									scenario.barChart,
									scenario.name
								),
								hasError: false
							};

							this._baselineScenarioComparisonBarChart$.next(
								this.baselineScenarioComparisonBarChart.chart
							);
						},
						error: () => {
							this.baselineScenarioComparisonBarChart = {
								chart: {} as BarChart,
								hasError: true
							};
						},
						finalize: () => {
							this.isBaselineScenarioComparisonBarChartLoading = false;
							this.scenarioRunForm.controls.scenario.enable({ emitEvent: false });
						}
					}),
					switchMap(() => this._fetchSectionsData() || of(null)),
					takeUntil(this._destroy$)
				)
				.subscribe();
		}
	}

	/**
	 * Merges the baseline and custom scenario bar charts into a single bar chart.
	 *
	 * This method combines the first bar series from the baseline scenario bar chart
	 * and the first bar series from the custom scenario bar chart into a new bar chart.
	 *
	 * @param customScenarioBarChart - The bar chart representing the custom scenario.
	 * @returns A new `BarChart` object containing the merged bar series from the baseline
	 *          and custom scenario bar charts.
	 */
	private _mergeBaselineAndCustomScenarioBarCharts(
		customScenarioBarChart: BarChart,
		scenarioName: string
	): BarChart {
		const baselineScenarioBarChart = this.baselineScenarioLayersBarChart!.chart;
		const baselineSeries = baselineScenarioBarChart.barSeries;
		const customSeries = customScenarioBarChart.barSeries;

		const mergedSeries = [
			{
				...baselineSeries[0],
				name: "Baseline scenario"
			},
			{
				...customSeries[0],
				name: scenarioName
			}
		];
		return { barSeries: mergedSeries };
	}

	/**
	 * Opens a confirmation dialog to delete the selected scenario.
	 * The dialog will display a message asking the user to confirm the deletion of the selected scenario.
	 * The dialog has two buttons: "Yes, Delete" to confirm the deletion and "Cancel" to close the dialog without deleting.
	 */
	public onDeleteScenario() {
		const scenarioName = this.scenariosChips.find(
			chip => chip.value === +this.selectedScenario
		)?.label;

		this._dialogService.open({
			data: {
				type: "confirm",
				confirmBtnText: "Yes, Delete",
				closeBtnText: "Cancel",
				title: `Are you sure you want to delete "${scenarioName}"?`,
				confirmAction: this._onConfirmDelete.bind(this)
			}
		});
	}

	/**
	 * Handles the confirmation of scenario deletion.
	 *
	 * This method retrieves the application ID from the form controls and the selected scenario ID.
	 * If the application ID is present, it calls the deleteScenario method of the _whatIfScenarioService
	 * to delete the scenario. Upon successful deletion, it updates the scenariosChips array by removing
	 * the deleted scenario.
	 *
	 * @private
	 */
	private _onConfirmDelete() {
		const applicationId = this.applicationForm.controls.applicationId.value;
		const scenarioId = +this.selectedScenario;

		if (applicationId) {
			this._whatIfScenarioService
				.deleteScenario(applicationId, scenarioId, this._tenantId)
				.pipe(
					tap({
						next: () => {
							this.scenariosChips = this.scenariosChips.filter(
								chip => chip.value !== scenarioId
							);

							if (this.scenariosChips.length === 0) {
								this.customScenarioLayers = [];
								this.selectedScenario = "";
							}

							this.isScenarioVisible = this.scenariosChips.length > 0;

							this.scenariosOptions = this.scenariosOptions.filter(
								option => option.value !== scenarioId.toString()
							);

							this.scenarioRunOptions = this.scenarioRunOptions.filter(
								option => option.value !== scenarioId.toString()
							);
						}
					}),
					takeUntil(this._destroy$)
				)
				.subscribe();
		}
	}

	/**
	 * Navigates to the WHAT_IF_CONFIGURATION path with the specified layer's name as a query parameter.
	 *
	 * @param {ScenarioLayer} [layer] - The scenario layer to be edited. If provided, its name will be used as a query parameter.
	 */
	public onEdit(layer?: ScenarioLayer) {
		const { applicationId, predictionWindow } = this.applicationForm.value;
		const scenarioId = this.selectedScenario;
		const layerId = layer?.name;
		const resourceType = this._router.url.split("/").pop();

		this._router.navigate([PATHS.WHAT_IF_CONFIGURATION], {
			queryParams: {
				resourceType,
				applicationId,
				predictionWindow,
				scenarioId,
				layerId
			}
		});
	}

	/**
	 * Handles the refresh of the baseline scenario comparison bar chart.
	 *
	 * This method retrieves scenario information based on the selected scenario
	 * and updates the baseline scenario comparison bar chart accordingly. It also
	 * manages the loading state and enables/disables the scenario selection form control.
	 *
	 * @returns A subscription to the scenario information retrieval observable.
	 *
	 * Behavior:
	 * - If `_applicationId` or `scenarioId` is not available, the method exits early.
	 * - Sets the loading state and disables the scenario selection form control.
	 * - Calls the `_whatIfScenarioService.getScenarioInfo` method to fetch scenario details.
	 * - Updates the chart data and visibility of the "Run Scenario" button based on the scenario status.
	 * - Handles errors by setting the chart to an error state.
	 * - Finalizes by resetting the loading state and re-enabling the scenario selection form control.
	 */
	public onBaselineScenarioComparisonBarChartRefresh() {
		const scenarioId = this.scenarioRunForm.controls.scenario.value;

		if (!this._applicationId || !scenarioId) return;

		this.isBaselineScenarioComparisonBarChartLoading = true;
		this.scenarioRunForm.controls.scenario.disable({ emitEvent: false });

		return this._whatIfScenarioService
			.getScenarioInfo(
				this._applicationId,
				+scenarioId,
				this._tenantId,
				this._predictionWindow
			)
			.pipe(
				tap({
					next: scenario => {
						this.isRunScenarioButtonVisible = scenario.status === "CREATED";

						if (!this.isRunScenarioButtonVisible) {
							this.baselineScenarioComparisonBarChart = {
								chart: this._mergeBaselineAndCustomScenarioBarCharts(
									scenario.barChart,
									scenario.name
								),
								hasError: false
							};

							this._baselineScenarioComparisonBarChart$.next(
								this.baselineScenarioComparisonBarChart.chart
							);

							this.isScenarioRunned = true;
						}
					},
					error: () => {
						this.baselineScenarioComparisonBarChart = {
							chart: {} as BarChart,
							hasError: true
						};
					},
					finalize: () => {
						this.isBaselineScenarioComparisonBarChartLoading = false;
						this.scenarioRunForm.controls.scenario.enable({ emitEvent: false });
					}
				}),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	/**
	 * Generates the overrides for the baseline scenario comparison bar chart.
	 *
	 * @param {BarChart} barChart - The bar chart object containing the series data.
	 * @returns The configuration overrides for the bar chart.
	 *
	 * The returned object contains:
	 * - `legend`: Customizes the legend with specific colors for "Baseline" and the second series in the bar chart.
	 * - `series`: Ensures that labels are shown on the series.
	 */
	private _getBaselineScenarioComparisonBarChartOverrides(barChart: BarChart): EChartsOption {
		return {
			legend: {
				show: true,
				data: [
					{
						name: barChart.barSeries[0].name,
						itemStyle: {
							color: COLORS.YELLOW
						}
					},
					{
						name: barChart.barSeries[1].name,
						itemStyle: {
							color: COLORS.YELLOW_100
						}
					}
				]
			},
			series: {
				label: {
					show: true
				}
			}
		};
	}

	/**
	 * Handles the status changes of the `newScenarioForm` and updates the state of the
	 * "Create Scenario" button accordingly. If the form status is "INVALID", the button
	 * will be disabled.
	 *
	 * @private
	 * @remarks
	 * This method subscribes to the `statusChanges` observable of the form and uses
	 * the `tap` operator to set the button's disabled state. The subscription is
	 * automatically unsubscribed when the component is destroyed using the `takeUntil`
	 * operator with the `_destroy$` subject.
	 */
	private _onCreateScenarioFormStatusChange() {
		this.newScenarioForm.statusChanges
			.pipe(
				tap({
					next: status => this._isCreateScenarioButtonDisabled.set(status === "INVALID")
				}),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}
}
